/**
 * Sign-in.
 *
 * Deny by default (spec section 4): there is no public view and no
 * self-service sign-up. Accounts are provisioned by an R5 in Supabase and must
 * additionally have an active `app_users` row, which the Worker checks on every
 * request — so a stray Supabase account gets a signed-in session and nothing to
 * look at, which is the correct outcome.
 */

import { useState, type FormEvent } from 'react';
import { useAuth } from '../../lib/auth';
import { Card } from '../../components/ui/primitives';
import { useT } from '../../i18n';

export function LoginPage() {
  const { signInWithPassword } = useAuth();
  const t = useT();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function onSubmit(event: FormEvent) {
    event.preventDefault();
    setError(null);
    setBusy(true);
    try {
      await signInWithPassword(email, password);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : t.auth.signInFailed);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="login">
      <Card className="login__card">
        <h1 style={{ marginBottom: 4 }}>{t.auth.title}</h1>
        <p className="muted small" style={{ marginTop: 0 }}>
          {t.auth.blurb}
        </p>

        <form onSubmit={onSubmit}>
          <div className="login__field">
            <label htmlFor="email">{t.auth.email}</label>
            <input
              id="email"
              type="email"
              autoComplete="username"
              required
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          </div>

          <div className="login__field">
            <label htmlFor="password">{t.auth.password}</label>
            <input
              id="password"
              type="password"
              autoComplete="current-password"
              required
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          </div>

          {error ? (
            <p className="small" style={{ color: 'var(--danger)' }} role="alert">
              {error}
            </p>
          ) : null}

          <button type="submit" className="btn btn--primary" disabled={busy} style={{ width: '100%' }}>
            {busy ? t.auth.signingIn : t.auth.signIn}
          </button>
        </form>
      </Card>
    </div>
  );
}
